import {
  createFileRoute,
  redirect,
  retainSearchParams,
  stripSearchParams,
} from '@tanstack/react-router'
import { isEqual, pick } from 'lodash/fp'
import * as v from 'valibot'

import { buildPageTitle } from '@/core/utils/buildPageTitle/buildPageTitle'
import useFeatures from '@/core/utils/features/useFeatures'
import { getLocalStorageItem } from '@/core/utils/localStorage/getLocalStorageItem'
import { setLocalStorageItem } from '@/core/utils/localStorage/setLocalStorageItem'
import { CoursesSummary } from '@/learnpaths-media-courses/components/CoursesSummary/CoursesSummary'
import { DialogProvider } from '@/learnpaths-summary/contexts/Dialog'
import { CONTENT_LOCALE } from '@/locale/constants'
import { NotFound } from '@/router/NotFound'

const storageKey = 'managed-courses-filters'
const searchSchema = v.object({
  categoryId: v.optional(v.string()),
  search: v.optional(v.string()),
  status: v.fallback(
    v.optional(v.picklist(['active', 'draft', 'inactive'])),
    undefined,
  ),
  language: v.optional(v.picklist(CONTENT_LOCALE)),
  types: v.fallback(
    v.optional(v.array(v.picklist(['linear', 'certificate']))),
    undefined,
  ),
  owner: v.optional(v.literal('myself')),
})
const filterKeys = Object.keys(searchSchema.entries) as Array<
  keyof v.InferInput<typeof searchSchema>
>

const defaultFilters = {
  types: [] as ('linear' | 'certificate')[],
}

export const Route = createFileRoute('/company/_management/courses/overview')({
  head: () => ({
    meta: [
      {
        title: buildPageTitle({
          titleKey: 'page-title-courses-overview',
        }),
      },
    ],
  }),
  validateSearch: searchSchema,
  beforeLoad: ({ cause, location, search }) => {
    const storedFilters = getLocalStorageItem(storageKey)

    const searchFilters = {
      ...pick(['categoryId', 'language', 'owner', 'status'], search),
      types: search.types ?? defaultFilters.types,
    }

    if (cause === 'stay') {
      if (!isEqual(storedFilters, searchFilters)) {
        setLocalStorageItem(storageKey, searchFilters)
      }
    }

    if (cause !== 'enter') return
    if (!location.state?.restoreFilters) return
    if (isEqual(storedFilters, searchFilters)) return

    throw redirect({
      to: '/company/courses/overview',
      search: storedFilters,
      replace: true,
    })
  },
  search: {
    middlewares: [
      stripSearchParams(defaultFilters),
      retainSearchParams(filterKeys),
    ],
  },

  component: CoursesOverview,
})

function CoursesOverview() {
  const isLearnpathAsCourseEnabled = useFeatures('learnpath-as-a-course')

  if (!isLearnpathAsCourseEnabled) {
    return <NotFound />
  }

  return (
    <DialogProvider>
      <CoursesSummary />
    </DialogProvider>
  )
}
